import delegate from "delegate";
import createHistory from "history/createBrowserHistory";
import nanobus from "../nanobus";
import expired from "expired";
import URL from "url-parse";

export const historyBus = nanobus();
// Mix the visit method in since it’s exposed as the API
historyBus.visit = visit;

const history = createHistory();
const pageCache = {};

export default function create(options = {}) {
  options = Object.assign({ cache: true }, options);
  const unlisten = history.listen(onHistoryChange.bind(this, options));
  // Cache the current page
  // Intercept all links
  delegate(document, "a", "click", onLinkClick);
  delegate(document, "form[method='get']", "submit", onFormSubmit);
  delegate(document, "form[method='GET']", "submit", onFormSubmit);
  document.addEventListener("historysubmit", onFormSubmit);
  // Return the API
  return historyBus;
}

function onLinkClick(e) {
  const ignore =
    e.delegateTarget.getAttribute("data-history-ignore") === "true" ||
    e.metaKey ||
    e.ctrlKey;
  if (!ignore) {
    const url = new URL(e.delegateTarget.href);
    const sameHost = url.host === window.location.host;
    const sameLocation = url.toString() === window.location.toString();
    if (sameHost) {
      e.preventDefault();
      if (!sameLocation) {
        visit(removeHostnameFromURL(url));
      }
    }
  }
}

function onFormSubmit(e) {
  const delegateTarget = e.delegateTarget || e.detail.delegateTarget;
  const ignore =
    delegateTarget.getAttribute("data-history-ignore") === "true" ||
    e.metaKey ||
    e.ctrlKey;
  if (!ignore) {
    const url = new URL(delegateTarget.getAttribute("action"));
    // Manually construct URLSearchParams
    let formData = new FormData(delegateTarget);
    let query = new URLSearchParams();
    for (var entry of formData.entries()) {
      query.append(entry[0], entry[1]);
    }
    query = query.toString();
    const sameHost = url.host === window.location.host;
    let urlWithQuery = url.toString();
    if (query !== "") {
      urlWithQuery = `${urlWithQuery}?${query}`;
    }
    const sameLocation = urlWithQuery === window.location.toString();
    if (sameHost) {
      e.preventDefault();
      if (!sameLocation) {
        visit(removeHostnameFromURL(urlWithQuery, url.origin));
      }
    }
  }
}

function finalizePath(currentPath, finalPath, state) {
  if (finalPath && currentPath !== finalPath) {
    history.replace(finalPath, state);
  }
}

function removeHostnameFromURL(url, origin) {
  origin = origin || url.origin;
  return url.toString().substr(origin.length);
}

function visit(location, state) {
  // TODO allow cancel?
  historyBus.emit("before-visit");
  history.push(location, state);
  historyBus.emit("visit");
}

function getScopeElement(scopeSelector) {
  return scopeSelector != null
    ? document.querySelector(scopeSelector)
    : document.body;
}

function getCachedPage(path) {
  const cachedPage = pageCache[path];
  var pageExpired = false;
  try {
    pageExpired = expired(cachedPage.headers);
  } catch (error) {
    pageExpired = true;
  }
  if (!cachedPage || pageExpired) {
    pageCache[path] = null;
    return false;
  } else {
    return cachedPage;
  }
}

function setCachedPage(path, html, headers) {
  // Set a date in the headers manually
  headers = {
    date: new Date().toGMTString(),
    ...headers
  };
  const pageExpired = expired(headers);
  if (!pageExpired) {
    pageCache[path] = { html, headers };
  }
}

let previousLocation = `${window.location.pathname}${window.location.search}`;
function onHistoryChange({ cache, scopeSelector }, location, action) {
  // Ignore any replace actions
  if (action === "REPLACE") {
    return;
  }
  // If there’s a scope, use it, otherwise replace the entire document
  let scope = getScopeElement(scopeSelector);
  const normalisedLocation = `${location.pathname}${location.search}`;
  if (previousLocation === normalisedLocation) return;
  // Store as a scope variable for access in the promise below
  const previousLocationLocal = previousLocation;
  previousLocation = normalisedLocation;
  fetchPage(normalisedLocation, action, cache)
    .then(({ html, headers }) => {
      historyBus.emit("before-render", scope, action, previousLocationLocal);
      getScopedResponse(html, scopeSelector)
        .then(rsp => {
          // Update the title
          updateTitle(rsp.title);
          // Finalize representative path
          if (headers["original-fullpath"]) {
            finalizePath(
              normalisedLocation,
              headers["original-fullpath"],
              location.state
            );
          }
          // Replace the DOM
          scope.outerHTML = rsp.html;
          // Refresh scope reference
          scope = getScopeElement(scopeSelector);
          // Handle any procedural bits
          activateScriptElements(scope);
          focusFirstAutofocusableElement(scope);
          historyBus.emit("after-render", scope, action, normalisedLocation);
        })
        .catch(error => {
          window.location = normalisedLocation;
        });
    })
    .catch(error => {
      window.location = normalisedLocation;
    });
}

function getScopedResponse(htmlString, scopeSelector) {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.onload = () => {
      try {
        iframe.style.display = "none";
        iframe.contentDocument.documentElement.innerHTML = htmlString;
        let node;
        const title = iframe.contentDocument.title;
        if (scopeSelector) {
          node = iframe.contentDocument.documentElement.querySelector(
            scopeSelector
          );
        } else {
          node = iframe.contentDocument.body;
        }
        resolve({
          html: node.outerHTML,
          title,
          iframe
        });
        iframe.parentNode.removeChild(iframe);
      } catch (error) {
        reject(error, htmlString);
      }
    };
    document.body.appendChild(iframe);
  });
}

function fetchPage(pathname, action, useCache) {
  return new Promise((resolve, reject) => {
    const cachedLocation = getCachedPage(pathname);
    if (cachedLocation && (action === "POP" || useCache)) {
      historyBus.emit("request-from-cache", action);
      resolve({ ...cachedLocation });
    } else {
      historyBus.emit("request-start", action);
      fetch(pathname, {
        credentials: "include",
        "Content-Type": "text/html",
        "X-HISTORY-MANAGER": true
      })
        .then(response => {
          const headers = {};
          response.headers.forEach((value, key) => (headers[key] = value));
          historyBus.emit("request-end", action);
          response.text().then(html => {
            setCachedPage(pathname, html, headers);
            resolve({ html, headers });
          });
        })
        .catch(error => {
          reject(error);
        });
    }
  });
}

function updateTitle(title) {
  document.title = title;
}

function activateScriptElements(scope) {
  const scriptElements = Array.prototype.slice.call(
    scope.querySelectorAll("script")
  );
  scriptElements.forEach(replaceableElement => {
    const element = createScriptElement(replaceableElement);
    replaceableElement.parentNode.replaceChild(element, replaceableElement);
  });
}

function createScriptElement(element) {
  if (element.getAttribute("data-history-eval") === "false") {
    return element;
  } else {
    const createdScriptElement = document.createElement("script");
    createdScriptElement.textContent = element.textContent;
    createdScriptElement.async = false;
    createdScriptElement.dataset.foo = "foo";
    copyElementAttributes(createdScriptElement, element);
    return createdScriptElement;
  }
}

function copyElementAttributes(destinationElement, sourceElement) {
  Array.prototype.slice
    .call(sourceElement.attributes)
    .forEach(({ name, value }) => {
      destinationElement.setAttribute(name, value);
    });
}

function focusFirstAutofocusableElement(scope) {
  const firstAutofocusableElement = scope.querySelector("[autofocus]");
  if (firstAutofocusableElement) {
    firstAutofocusableElement.focus();
  }
}

/**
 * Create and dispatch a custom event that is cancelable
 * @param {DOMNode} target Event target
 */
export function submit(form, target) {
  target = target || form;
  const event = new window.CustomEvent("historysubmit", {
    detail: {
      delegateTarget: form
    },
    bubbles: true,
    cancelable: true
  });
  const succeeded = target.dispatchEvent(event);
  return !succeeded;
}
